export type PdfTextRun = {
  text: string;
  isBold: boolean;
  isItalic: boolean;
};

export type PdfLineForMarkers = {
  text: string;
  runs?: PdfTextRun[];
};

export type PdfAppendixMarker = {
  searchText: string;
  marker: string;
  label: string;
};

export const buildPdfCommentMarkers = (
  comments: Array<{ selectedText?: string }>,
): PdfAppendixMarker[] => {
  return comments.map((comment, index) => {
    const number = index + 1;
    const marker = `[c${number}]`;
    return {
      searchText: comment.selectedText || '',
      marker,
      label: marker,
    };
  });
};

export const buildPdfSuggestionMarkers = (
  suggestions: Array<{ originalText: string }>,
): PdfAppendixMarker[] => {
  return suggestions.map((suggestion, index) => {
    const number = index + 1;
    const marker = `[s${number}]`;
    return {
      searchText: suggestion.originalText || '',
      marker,
      label: marker,
    };
  });
};

export const findNextUnoccupiedMatch = (
  plainText: string,
  searchText: string,
  occupied: Array<{ start: number; end: number }>,
): { start: number; end: number } | null => {
  if (!searchText) {
    return null;
  }

  const overlapsOccupied = (start: number, end: number): boolean => {
    return occupied.some((range) => start < range.end && end > range.start);
  };

  let cursor = 0;
  while (cursor <= plainText.length) {
    const start = plainText.indexOf(searchText, cursor);
    if (start < 0) {
      return null;
    }
    const end = start + searchText.length;
    if (!overlapsOccupied(start, end)) {
      return { start, end };
    }
    cursor = start + 1;
  }

  return null;
};

export const insertMarkerAtOffsetInRuns = (
  runs: PdfTextRun[],
  insertAt: number,
  marker: string,
): PdfTextRun[] => {
  const nextRuns: PdfTextRun[] = [];
  let offset = 0;
  let inserted = false;

  for (const run of runs) {
    const runStart = offset;
    const runEnd = offset + run.text.length;

    if (!inserted && insertAt >= runStart && insertAt <= runEnd) {
      const localInsert = insertAt - runStart;
      const before = run.text.slice(0, localInsert);
      const after = run.text.slice(localInsert);
      if (before) {
        nextRuns.push({ text: before, isBold: run.isBold, isItalic: run.isItalic });
      }
      nextRuns.push({ text: marker, isBold: false, isItalic: false });
      inserted = true;
      if (after) {
        nextRuns.push({ text: after, isBold: run.isBold, isItalic: run.isItalic });
      }
    } else {
      nextRuns.push(run);
    }

    offset = runEnd;
  }

  if (!inserted) {
    nextRuns.push({ text: marker, isBold: false, isItalic: false });
  }

  return nextRuns;
};

export const annotatePdfLinesWithMarkers = <T extends PdfLineForMarkers>(
  lines: T[],
  markers: PdfAppendixMarker[],
): T[] => {
  const annotated = lines.map((line) => ({
    ...line,
    runs: line.runs ? line.runs.map((run) => ({ ...run })) : undefined,
  }));

  type LineOccupation = {
    occupied: Array<{ start: number; end: number }>;
  };
  const occupations: LineOccupation[] = annotated.map(() => ({ occupied: [] }));

  for (const marker of markers) {
    if (!marker.searchText) {
      continue;
    }

    for (let lineIndex = 0; lineIndex < annotated.length; lineIndex += 1) {
      const line = annotated[lineIndex];
      const runs =
        line.runs && line.runs.length > 0
          ? line.runs
          : [{ text: line.text, isBold: false, isItalic: false }];
      const plainText = runs.map((run) => run.text).join('');
      const match = findNextUnoccupiedMatch(plainText, marker.searchText, occupations[lineIndex].occupied);
      if (!match) {
        continue;
      }

      const updatedRuns = insertMarkerAtOffsetInRuns(runs, match.end, marker.marker);
      line.runs = updatedRuns;
      line.text = updatedRuns.map((run) => run.text).join('');
      const markerLength = marker.marker.length;
      occupations[lineIndex].occupied = occupations[lineIndex].occupied.map((range) => {
        if (range.start >= match.end) {
          return {
            start: range.start + markerLength,
            end: range.end + markerLength,
          };
        }
        return range;
      });
      occupations[lineIndex].occupied.push(match);
      break;
    }
  }

  return annotated;
};
